import { useEffect, useState } from "react";
import {
  getChapterAddendumAssociations,
  getEpisodeAddendumAssociations,
} from "../services/api/attachments/addendum.api";

const formatAddendumAssociations = (response, addendumId, isChapter) => {
  const entries = Array.isArray(response) ? response : [];
  const entry =
    entries.find((item) => item?.addendumId === addendumId) ?? entries[0];
  const associations = (isChapter ? entry?.chapters : entry?.episodes) ?? [];

  const numbers = associations
    .map((item) => item?.number)
    .filter((number) => number != null)
    .sort((a, b) => a - b)
    .map((number) => String(number).padStart(2, "0"));

  if (!numbers.length) return "";

  return `${isChapter ? "CAP." : "EP."} ${numbers.join(", ")}`;
};

/**
 * Fetches and formats the chapter/episode associations label for an addendum.
 *
 * @param {object} params
 * @param {boolean} params.isAdendo - Whether the attachment type is "Adendo".
 * @param {number|string|null} params.addendumId - The addendum attachment id.
 * @param {boolean} params.isChapter - True for chapter-based works (Dramaturgia Longa), false for episodes.
 * @returns {string} Formatted label, e.g. "CAP. 01, 02" or "EP. 03".
 */
export const useAddendumAssociations = ({ isAdendo, addendumId, isChapter }) => {
  const [addendumAssociations, setAddendumAssociations] = useState("");
  const [isLoadingAddendumAssociations, setIsLoadingAddendumAssociations] =
    useState(false);

  useEffect(() => {
    if (!isAdendo || addendumId == null) {
      setAddendumAssociations("");
      setIsLoadingAddendumAssociations(false);
      return;
    }

    let active = true;
    const fetchAssociations = isChapter
      ? getChapterAddendumAssociations
      : getEpisodeAddendumAssociations;

    setIsLoadingAddendumAssociations(true);
    fetchAssociations({ addendumIds: [addendumId] })
      .then((response) => {
        if (!active) return;
        setAddendumAssociations(
          formatAddendumAssociations(response, addendumId, isChapter)
        );
      })
      .catch(() => {
        if (active) setAddendumAssociations("");
      })
      .finally(() => {
        if (active) setIsLoadingAddendumAssociations(false);
      });

    return () => {
      active = false;
    };
  }, [isAdendo, addendumId, isChapter]);

  return { addendumAssociations, isLoadingAddendumAssociations };
};
